import type { DataKeyEdgeType } from '@/edges/types';
import type { AppNode } from '@/nodes/types';
import { considerationTypesByIdState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { getGuideItemId, type GuideNodeId, outgoingHandles } from '@rock/types';
import { BaseEdge, type EdgeProps, getBezierPath, useReactFlow } from '@xyflow/react';
import { type FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';
import { getItemTypeColors } from '../util/getItemTypeColors';

const DataKeyEdge: FC<EdgeProps<DataKeyEdgeType>> = ({
    data,
    source,
    sourceHandleId,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
}) => {
    const { getNode } = useReactFlow<AppNode>();
    const sourceNode = useMemo(() => getNode(source), [source, getNode]);

    const { spaceId } = useContext(FlowParamsContext);
    const considerationTypesById = useAsyncAtomValue(considerationTypesByIdState(spaceId));

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const items = useMemo(
        () => [...(data?.items?.equipment ?? []), ...(data?.items?.considerations ?? [])],
        [data?.items],
    );
    const textHeight = 22; // Height per text
    const paddingX = 10; // Horizontal padding for width
    const gap = 6;

    // Store measured widths
    const textRefs = useRef<(SVGTextElement | null)[]>([]);
    const [textWidths, setTextWidths] = useState<number[]>([]);

    // Measure text width when components change or when consideration types load
    // biome-ignore lint/correctness/useExhaustiveDependencies(considerationTypesById): Need to trigger effect when types load
    useEffect(() => {
        const newWidths = items.map((_, index) => {
            const textEl = textRefs.current[index];
            return textEl ? textEl.getBBox().width + paddingX * 2 : 60;
        });
        setTextWidths(newWidths);
    }, [items, considerationTypesById]);

    // 🔥 Calculate total height of badge group for perfect centering
    const totalBadgeHeight = items.length * (textHeight + gap) - gap; // Remove last gap
    const startY = labelY - totalBadgeHeight / 2 + textHeight / 2; // Shift upward to center

    return (
        <>
            <BaseEdge path={edgePath} markerEnd={markerEnd} />
            <text
                x={sourceX + 5}
                y={sourceY}
                fill="#006699"
                fontSize="8px"
                textAnchor="start"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
            >
                {getLabelFromSourceNode(sourceNode, sourceHandleId)}
            </text>

            {/* Individual badges per component with measured widths */}
            {items.map((item, index) => {
                const yOffset = startY + index * (textHeight + gap);
                const rectWidth = textWidths[index] ?? 60; // Default width
                const category = item.category;
                const { stroke, fill } = getItemTypeColors(category);
                const itemId = getGuideItemId(item);

                // For consideration items, show title instead of ID
                let displayText: string = itemId;
                if (item.category === 'consideration' && considerationTypesById) {
                    const considerationType = considerationTypesById[item.considerationTypeId];
                    displayText = considerationType?.title ?? itemId;
                }

                return (
                    <g key={`${item.category}:${itemId}`}>
                        <rect
                            className={stroke}
                            x={labelX - rectWidth / 2}
                            y={yOffset - textHeight / 2}
                            width={rectWidth}
                            height={textHeight}
                            rx="8"
                            ry="8"
                            fill="white"
                            strokeWidth="1"
                            opacity="0.8"
                            style={{ pointerEvents: 'all' }}
                        />
                        <text
                            ref={(el) => {
                                textRefs.current[index] = el;
                            }}
                            className={fill}
                            x={labelX}
                            y={yOffset + 1}
                            fontSize="12px"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ userSelect: 'none', pointerEvents: 'all' }}
                        >
                            {displayText}
                        </text>
                    </g>
                );
            })}
        </>
    );
};

function getLabelFromSourceNode(
    node: AppNode | undefined,
    sourceHandleId: string | null | undefined,
): string | undefined {
    switch (node?.type) {
        case 'select': {
            const selectHandles = outgoingHandles.select(node.id as GuideNodeId, node.data);
            if (selectHandles.type !== 'single') {
                return;
            }
            const sourceOption = node.data.sections
                .flatMap((section, sectionIndex) =>
                    section.options.map((option, optionIndex) => ({
                        ...option,
                        sectionIndex,
                        optionIndex,
                    })),
                )
                .find(
                    (option) =>
                        sourceHandleId === selectHandles.sectionOptions[option.sectionIndex]![option.optionIndex],
                );
            return `${node.data.dataKey} = ${sourceOption?.value}`;
        }

        case 'yes-no': {
            const yesNoHandles = outgoingHandles['yes-no'](node.id as GuideNodeId, node.data);
            if (sourceHandleId === yesNoHandles.yes) {
                return `${node.data.dataKey} = true`;
            }
            if (sourceHandleId === yesNoHandles.no) {
                return `${node.data.dataKey} = false`;
            }
        }
    }
}

export default DataKeyEdge;
